/**
 * Cookie management utility for Raidu website
 * Handles setting, getting, and managing cookie consent preferences
 */

// Cookie categories and their default states
export type CookieCategory = 'necessary' | 'functional' | 'analytics' | 'marketing';

export interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

// Storage keys
export const CONSENT_STORAGE_KEY = 'raidu_cookie_consent';
export const PREFERENCES_STORAGE_KEY = 'raidu_cookie_preferences';

// Default preferences (only necessary cookies enabled by default)
export const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
};

/**
 * Get the current cookie consent status
 * @returns boolean indicating if consent has been given, or null if not set
 */
export const getConsentStatus = (): boolean | null => {
  const savedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
  return savedConsent ? savedConsent === 'true' : null;
};

/**
 * Get the current cookie preferences
 * @returns CookiePreferences object with current settings
 */
export const getCookiePreferences = (): CookiePreferences => {
  const savedPreferences = localStorage.getItem(PREFERENCES_STORAGE_KEY);
  
  if (savedPreferences) {
    try {
      const parsedPreferences = JSON.parse(savedPreferences) as Partial<CookiePreferences>;
      return {
        ...DEFAULT_PREFERENCES,
        ...parsedPreferences,
        necessary: true, // Always ensure necessary cookies are enabled
      };
    } catch (error) {
      console.error('Error parsing saved cookie preferences:', error);
    }
  }
  
  return DEFAULT_PREFERENCES;
};

/**
 * Save cookie preferences to local storage
 * @param preferences CookiePreferences to save
 * @param hasConsented boolean indicating if user has given consent
 */
export const saveCookiePreferences = (
  preferences: CookiePreferences, 
  hasConsented: boolean
): void => {
  // Ensure necessary cookies are always enabled
  const updatedPreferences = {
    ...preferences,
    necessary: true,
  };

  localStorage.setItem(CONSENT_STORAGE_KEY, String(hasConsented));
  localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(updatedPreferences));
};

/**
 * Check if a specific cookie category is enabled
 * @param category The cookie category to check
 * @returns boolean indicating if the category is enabled
 */
export const isCategoryEnabled = (category: CookieCategory): boolean => {
  const preferences = getCookiePreferences();
  return preferences[category];
};

/**
 * Clear all cookie preferences (used for testing or resetting)
 */
export const clearCookiePreferences = (): void => {
  localStorage.removeItem(CONSENT_STORAGE_KEY);
  localStorage.removeItem(PREFERENCES_STORAGE_KEY);
};

/**
 * Set a cookie with the given name, value, and expiration days
 * Only sets the cookie if the appropriate category is enabled
 * @param name Cookie name
 * @param value Cookie value
 * @param days Days until expiration
 * @param category Cookie category this belongs to
 */
export const setCookie = (
  name: string,
  value: string,
  days: number,
  category: CookieCategory
): void => {
  // Only set cookie if the category is enabled
  if (!isCategoryEnabled(category)) {
    return;
  }

  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `; expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value}${expires}; path=/; SameSite=Lax`;
};

/**
 * Get a cookie value by name
 * @param name Cookie name
 * @returns Cookie value or empty string if not found
 */
export const getCookie = (name: string): string => {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  
  return '';
};

/**
 * Delete a cookie by name
 * @param name Cookie name
 */
export const deleteCookie = (name: string): void => {
  document.cookie = `${name}=; Max-Age=-99999999; path=/`;
};
